import React, { Fragment, useState, useEffect, useCallback } from 'react'
import { css, StyleSheet } from 'aphrodite/no-important'
import { v4 as uuidv4 } from 'uuid'
import PropTypes from 'prop-types'
import debounce from 'lodash.debounce'
import { DatePicker } from '../date-picker'
import { PopupMenu } from '../popup-menu'
import { Checkbox, RadioButton, TextInput } from '../form/input'
import { ALL_FILTERS, filterNames } from './grid-commons'

const getSortIcon = (sortDirection) => (
  <svg
    width='11'
    height='13'
    viewBox='0 0 11 13'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M5.49999 0L10.2631 5.49999H0.736859L5.49999 0Z'
      fill={sortDirection === 1 ? '#0080FF' : '#96C8FF'}
    />
    <path
      d='M5.49995 12.8333L0.73682 7.33332L10.2631 7.33332L5.49995 12.8333Z'
      fill={sortDirection === -1 ? '#0080FF' : '#96C8FF'}
    />
  </svg>
)

const propTypes = {
  icons: PropTypes.object,
  headerDef: PropTypes.object,
  mergeHeaderRows: PropTypes.bool,
  headerHeight: PropTypes.number,
  gridWidth: PropTypes.number,
  filterValue: PropTypes.string,
  selectedItems: PropTypes.any,
  selectAllChecked: PropTypes.bool,
  selectAllIndeterminate: PropTypes.bool,
  isSelectFilterExcludeMode: PropTypes.bool,
  onPinChange: PropTypes.func,
  onFilterToggle: PropTypes.func,
  onFilterTypeUpdate: PropTypes.func,
  onChangeTextAlignment: PropTypes.func,
  onApplyFilter: PropTypes.func,
  onResetCol: PropTypes.func,
  onSort: PropTypes.func,
  filterType: PropTypes.oneOf(filterNames),
  onFilterChange: PropTypes.func,
  theme: PropTypes.object
}

const GridHeaderCell = (props) => {
  const { theme, headerDef, icons, gridWidth, selectItems, onSelectAllChange } =
    props

  // Refs
  const columnMenu = React.createRef()
  const filterDropdown = React.createRef()
  const pinDropdown = React.createRef()
  const selectMenu = React.createRef()

  const {
    gridHeaderBackground,
    gridHeaderTextColor,
    gridHeaderRightBorderColor,
    gridHeaderBottomBorderColor,
    gridHeaderFilterFontSize,
    gridHeaderHeight,
    gridHeaderFilterMenuBackground,
    gridHeaderFilterMenuTextColor,
    gridHeaderFilterMenuBorderColor
  } = theme

  const {
    colCellsStyles,
    text,
    pin,
    filter,
    field,
    dataType,
    sortable,
    sortOrder,
    colStyles,
    disabledFilterTypes,
    isCustomFilter,
    filterHint,
    filterType,
    showFilterHint
  } = headerDef

  const { width, textAlign, menuWidth = 233 } = colStyles

  // ------------------------ State variables ------------------------
  const [isFilterMenuVisible, setFilterMenuVisibility] = useState(false)
  const [isBarMenuVisible, setBarMenuVisibility] = useState(false)
  const [filterValue, setFilterValue] = useState(props.filterValue)
  const [isSubFilterMenuVisible, setSubFilterMenuVisibility] = useState(false)
  const [isSubPinMenuVisible, setSubPinMenuVisibility] = useState(false)
  const [isSelectMenuOpen, setIsSelectMenuOpen] = useState(false)
  const [selectFilterSerachValue, setSelectFilterSearchValue] = useState('')

  // ------------------------ Effects (userEffect hook) ------------------------
  useEffect(() => {
    if (props.filterValue === '' || !props.filterValue) {
      setFilterValue(props.filterValue)
    }
  }, [props.filterValue])

  // ------------------------ Styles ------------------------
  const styles = StyleSheet.create({
    header: {
      // display: 'flex',
      justifyContent: 'space-between',
      width: `${width - 1}px`,
      height: gridHeaderHeight,
      paddingLeft: '4px',
      textAlign,
      background: gridHeaderBackground,
      color: gridHeaderTextColor,
      flexGrow: '0',
      flexShrink: '0',
      fontWeight: '700',
      borderRight: `1px solid ${gridHeaderRightBorderColor}`,
      borderBottom: `1px solid ${gridHeaderBottomBorderColor}`,
      ':last-child': {
        borderRight: 'none'
      }
    },
    filterTypeDropdown: {
      display: 'flex',
      position: 'relative',
      background: gridHeaderFilterMenuBackground,
      border: `1px solid ${gridHeaderFilterMenuBorderColor}`,
      borderRadius: '2px',
      width: '232px',
      marginLeft: '8px',
      marginTop: '8px',
      fontSize: gridHeaderFilterFontSize
    },
    filterDropdownText: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      width: '200px',
      color: gridHeaderFilterMenuTextColor,
      fontWeight: '600',
      fontSize: gridHeaderFilterFontSize
    },
    filterDropdownBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '32px',
      height: '32px',
      borderLeft: `1px solid ${gridHeaderFilterMenuBorderColor}`,
      color: gridHeaderFilterMenuTextColor
    },
    filterHintIcon: {
      color: '#0080FF',
      fontSize: '13px',
      fontWeight: 'bold'
    },
    dropdownMenu: {
      width: '228px',
      background: '#103A60',
      border: '1px solid #0050A0',
      boxShadow: '0px 2px 4px rgb(0 0 0 / 50%)',
      maxHeight: '200px',
      overflowY: 'auto'
    }
  })

  const toggleColumnMenu = (e) => {
    e.stopPropagation()
    setBarMenuVisibility(!isBarMenuVisible)
  }

  const handleOnApplyFilter = () => {
    const { dataType, filterType, field } = props.headerDef
    props.onApplyFilter(dataType, filterType, field, filterValue)
  }

  const debouncedApplyStringFilter = useCallback(
    debounce((dataType, filterType, field, value) => {
      props.onApplyFilter(dataType, filterType, field, value)
    }, 500),
    []
  )

  const handleOnFilterValueChanged = (e) => {
    const { value } = e.target
    const { filterType, field, dataType } = props.headerDef
    setFilterValue(value)
    // debounce(() => { props.onApplyFilter(dataType, filterType, field, value); }, 500)();
    debouncedApplyStringFilter(dataType, filterType, field, value)
  }

  const handleOnDateChanged = (year, month, date) => {
    const { filterType, field, dataType } = props.headerDef
    const tmpMonth = (month + 1).toString().padStart(2, '0')
    const tmpDate = date.toString().padStart(2, '0')
    // const value = `${month + 1}/${date}/${year}`;
    const value = `${tmpMonth}/${tmpDate}/${year}`
    setFilterValue(value)
    // handleOnApplyFilter();
    props.onApplyFilter(dataType, filterType, field, value)
  }

  const handleOnChangeLowerRange = (e) => {
    const { value } = e.target
    const ranges = filterValue === '' ? filterValue : filterValue.split(':')
    setFilterValue(
      Array.isArray(ranges) ? `${value}:${ranges[1]}` : `${value}:`
    )
    // checkRangeBounds(); // TODO: Implement this method is necessary
  }

  const handleOnChangeUpperRange = (e) => {
    const { value } = e.target
    const ranges = filterValue === '' ? filterValue : filterValue.split(':')
    setFilterValue(
      Array.isArray(ranges) ? `${ranges[0]}:${value}` : `:${value}`
    )
    // checkRangeBounds(); // TODO: Implement this method is necessary
  }

  const handleOnDateRangeChanged = (pos, value) => {
    // pos 0 -> lower, pos 1 -> upper
    if (pos === 0) {
      const ranges = filterValue === '' ? filterValue : filterValue.split(':')
      setFilterValue(
        Array.isArray(ranges)
          ? `${value}:${
              ranges[1] ? ranges[1] : new Date().toLocaleDateString()
            }`
          : `${value}:`
      )
    } else if (pos === 1) {
      const { filterType, field, dataType } = props.headerDef
      const ranges = filterValue === '' ? filterValue : filterValue.split(':')
      setFilterValue(
        Array.isArray(ranges) ? `${ranges[0]}:${value}` : `:${value}`
      )
      // handleOnApplyFilter();
      props.onApplyFilter(
        dataType,
        filterType,
        field,
        Array.isArray(ranges) ? `${ranges[0]}:${value}` : `:${value}`
      )
    }
  }

  const handleOnChangeSelectFilterSearchValue = (e) => {
    const { value } = e.target
    setSelectFilterSearchValue(value)
  }

  let filterText = filter ? ALL_FILTERS[headerDef.filterType] : 'No Filter'
  let pinText = ''

  if (isCustomFilter) {
    filterText = filter ? filterHint : 'No Filter'
  }

  switch (pin) {
    case 'left':
      pinText = 'Pin to LEFT'
      break
    case 'right':
      pinText = 'pin to RIGHT'
      break
    case 'none':
    default:
      pinText = 'No Pin'
  }

  let lowerRange = ''
  let upperRange = ''
  let lowerDateVal = ''
  let upperDateVal = ''
  let lowerDate = ''
  let upperDate = ''
  let isLowerDatePristine = false
  let isUpperDatePrisitine = false

  if (headerDef.filterType === 'numberRange') {
    const ranges = filterValue.split(':')
    lowerRange = Array.isArray(ranges) ? ranges[0] : ''
    upperRange = Array.isArray(ranges) ? ranges[1] : ''
  }

  if (headerDef.filterType === 'dateRange') {
    const ranges =
      filterValue.indexOf(':') > 0 ? filterValue.split(':') : ['', '']
    if (ranges) {
      lowerDateVal = Array.isArray(ranges) ? ranges[0] : ''
      upperDateVal = Array.isArray(ranges) ? ranges[1] : ''
      lowerDate = lowerDateVal !== '' ? new Date(lowerDateVal) : new Date()
      upperDate = upperDateVal !== '' ? new Date(upperDateVal) : new Date()
      isLowerDatePristine = lowerDateVal.length === 0
      isUpperDatePrisitine = upperDateVal.length === 0
    } else {
      lowerDateVal = ''
      upperDateVal = ''
      lowerDate = new Date()
      upperDate = new Date()
      isLowerDatePristine = true
      isUpperDatePrisitine = true
    }
  }

  let selectFilterText = ''
  if (filter && filterType === 'select') {
    for (let i = 0; i < selectItems.length; i++) {
      if (
        selectItems[i].checked &&
        selectItems[i].value
          .toLowerCase()
          .startsWith(selectFilterSerachValue.toLowerCase())
      ) {
        selectFilterText += selectItems[i].value + ','
      }

      if (selectFilterText.length >= 10) {
        break
      }
    }
  }
  // console.log(`selectFilterText = ${selectFilterText}`);

  return (
    <>
      <div
        className={`${filter ? 'filtered' : ''} ${css(styles.header)}`}
        key={`${props.dataId}_${text}`}>
        {/* Top row --> Column name, sort icon and ellipsis icon */}
        <div
          className='fx header-top-row'
          style={{
            height: props.mergeHeaderRows ? `${props.headerHeight}px` : '24px',
            minHeight: props.mergeHeaderRows ? `${props.headerHeight}px` : '',
            width: '100%'
            // justifyContent: 'space-between'
          }}>
          {/* Column name */}
          <div className='fx' style={{ marginTop: '-4px' }}>
            <div
              className={`noselect ${filter ? 'text-truncate' : ''}`}
              style={{
                height: filter ? '26px' : 'auto',
                maxHeight: filter ? '26px' : 'auto',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                marginRight: '4px',
                flexGrow: '1'
              }}>
              {text}
            </div>
            {/* Sort icon */}
            {sortable ? (
              <div
                style={{
                  width: '20px',
                  height: filter ? '26px' : 'auto',
                  maxHeight: filter ? '26px' : 'auto'
                }}
                className='fx fx__hv-center'
                onClick={() => {
                  props.onSort(field)
                }}>
                {getSortIcon(sortOrder)}
              </div>
            ) : null}
          </div>
          {/* Ellipsis menu icon */}
          <div
            ref={columnMenu}
            style={{
              width: '20px',
              height: '24px',
              position: 'relative',
              display: props.hideGridHeaderMenus ? 'none' : ''
            }}
            className='fx fx__hv-center'
            onClick={toggleColumnMenu}>
            <i
              className={`cursor__pointer ${icons.menuIcon.class}`}
              style={{
                fontSize: '12px',
                marginRight: '4px'
              }}
            />
            <PopupMenu
              top={48}
              left={pin === 'left' ? 1 : null}
              right={pin !== 'left' ? 1 : null}
              visible={isBarMenuVisible}
              parent={columnMenu}
              onOutsideClick={() => {
                setBarMenuVisibility(false)
              }}
              position='absolute'
              // enableDyncmicPosition
              offsetLeft={320}
              parentContainerWidth={parseInt(gridWidth)}>
              {/* Filter menu root container (every thing realted to filter appears in this container) */}
              <div className='qtx-popup-menu' style={{ width: '250px' }}>
                {/* Filter combobox */}
                <div
                  ref={filterDropdown}
                  className={css(styles.filterTypeDropdown)}
                  onClick={(e) => {
                    e.stopPropagation()
                    setSubFilterMenuVisibility(!isSubFilterMenuVisible)
                    setSubPinMenuVisibility(false)
                  }}>
                  <div className='fx cursor__pointer'>
                    {/* Text */}
                    <div className={`pl-1 ${css(styles.filterDropdownText)}`}>
                      {filterText}
                    </div>
                    {/* Icon */}
                    <div className={css(styles.filterDropdownBtn)}>
                      <i className='fa fa-chevron-down' />
                    </div>
                  </div>
                </div>
                {/* Filter dropdown menu */}
                {isSubFilterMenuVisible ? (
                  <PopupMenu
                    top={45}
                    left={10}
                    position='absolute'
                    visible={isSubFilterMenuVisible}
                    parent={filterDropdown}
                    onOutsideClick={() => {
                      setSubFilterMenuVisibility(false)
                    }}>
                    <div className={css(styles.dropdownMenu)}>
                      {/* No Filter */}
                      <div
                        className='m-item no-hover pl-4'
                        onClick={() => {
                          console.log('No filter')
                          console.log('field', field)
                          console.log('filterValue', filterValue)
                          console.log('filter', filter)
                          props.onFilterToggle(field, false)
                        }}>
                        <div>
                          <RadioButton
                            text={
                              !isCustomFilter
                                ? 'No Filter (All Enteries)'
                                : 'Filter OFF'
                            }
                            isSelected={filter === false}
                          />
                        </div>
                      </div>

                      {/* Custom string filters */}
                      {isCustomFilter && dataType === 'string' ? (
                        <div
                          className='m-item no-hover pl-4'
                          onClick={() => {
                            console.log('Filter on')
                            console.log('field', field)
                            console.log('filterValue', filterValue)
                            console.log('filter', filter)
                            props.onFilterToggle(field, true)
                            filterText = filterHint
                          }}>
                          <div
                            onClick={() => {
                              props.onFilterToggle(field, true)
                            }}>
                            <RadioButton
                              text='Filter ON'
                              isSelected={headerDef.filter}
                            />
                          </div>
                        </div>
                      ) : null}

                      {/* Number filters */}
                      {!isCustomFilter && dataType === 'number' ? (
                        <Fragment>
                          {/* > Greater Than */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              props.onFilterTypeUpdate(field, 'greaterThan')
                            }}>
                            <div>
                              <RadioButton
                                text='> Greater Than'
                                isSelected={
                                  headerDef.filterType === 'greaterThan'
                                }
                              />
                            </div>
                          </div>
                          {/* >= Greater then or equals to  */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              console.log('No filter')
                              console.log('field', field)
                              console.log('filterValue', filterValue)
                              console.log('filter', filter)
                              props.onFilterTypeUpdate(
                                field,
                                'greaterThanOrEqualsTo'
                              )
                            }}>
                            <div>
                              <RadioButton
                                text='≥ Greater Than Or Equals To'
                                isSelected={
                                  headerDef.filterType ===
                                  'greaterThanOrEqualsTo'
                                }
                              />
                            </div>
                          </div>
                          {/* = Equals To */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              console.log('No filter')
                              console.log('field', field)
                              console.log('filterValue', filterValue)
                              console.log('filter', filter)
                              props.onFilterTypeUpdate(field, 'equalsTo')
                            }}>
                            <div>
                              <RadioButton
                                text='= Equals To'
                                isSelected={headerDef.filterType === 'equalsTo'}
                              />
                            </div>
                          </div>
                          {/* <= On OR Before Date */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              console.log('No filter')
                              console.log('field', field)
                              console.log('filterValue', filterValue)
                              console.log('filter', filter)
                              props.onFilterTypeUpdate(
                                field,
                                'lessThanOrEqualsTo'
                              )
                            }}>
                            <div>
                              <RadioButton
                                text='≤ Less Than Or Equals To'
                                isSelected={
                                  headerDef.filterType === 'lessThanOrEqualsTo'
                                }
                              />
                            </div>
                          </div>
                          {/* < Before Date */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              console.log('No filter')
                              console.log('field', field)
                              console.log('filterValue', filterValue)
                              console.log('filter', filter)
                              props.onFilterTypeUpdate(field, 'lessThan')
                            }}>
                            <div>
                              <RadioButton
                                text='< Less Than'
                                isSelected={headerDef.filterType === 'lessThan'}
                              />
                            </div>
                          </div>
                          {/* [] Range */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              console.log('No filter')
                              console.log('field', field)
                              console.log('filterValue', filterValue)
                              console.log('filter', filter)
                              props.onFilterTypeUpdate(field, 'numberRange')
                            }}>
                            <div>
                              <RadioButton
                                text='[ ] Range'
                                isSelected={
                                  headerDef.filterType === 'numberRange'
                                }
                              />
                            </div>
                          </div>
                          {/* # of Days Ago */}
                        </Fragment>
                      ) : null}

                      {/* Standard string filters */}
                      {!isCustomFilter && dataType === 'string' ? (
                        <Fragment>
                          {/* ‘Starts with’ text filter */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              if (!disabledFilterTypes.includes('startsWith')) {
                                props.onFilterTypeUpdate(field, 'startsWith')
                              }
                            }}>
                            <div>
                              <RadioButton
                                text='‘Starts with’ text filter'
                                isSelected={
                                  headerDef.filterType === 'startsWith'
                                }
                                isDisabled={disabledFilterTypes.includes(
                                  'startsWith'
                                )}
                              />
                            </div>
                          </div>

                          {/* ‘Contains’ text filter */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              if (!disabledFilterTypes.includes('contains')) {
                                props.onFilterTypeUpdate(field, 'contains')
                              }
                            }}>
                            <div>
                              <RadioButton
                                text='‘Contains’ text filter'
                                isSelected={
                                  filter && headerDef.filterType === 'contains'
                                }
                                isDisabled={disabledFilterTypes.includes(
                                  'contains'
                                )}
                              />
                            </div>
                          </div>

                          {/* ‘Exact match’ text filter */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              if (!disabledFilterTypes.includes('exactMatch')) {
                                props.onFilterTypeUpdate(field, 'exactMatch')
                              }
                            }}>
                            <div>
                              <RadioButton
                                text='‘Exact match’ text filter'
                                isSelected={
                                  filter &&
                                  headerDef.filterType === 'exactMatch'
                                }
                                isDisabled={disabledFilterTypes.includes(
                                  'exactMatch'
                                )}
                              />
                            </div>
                          </div>

                          {/* ‘Ends with’ text filter */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              if (!disabledFilterTypes.includes('endsWith')) {
                                props.onFilterTypeUpdate(field, 'endsWith')
                              }
                            }}>
                            <div>
                              <RadioButton
                                text='‘Ends with’ text filter'
                                isSelected={
                                  filter && headerDef.filterType === 'endsWith'
                                }
                                isDisabled={disabledFilterTypes.includes(
                                  'endsWith'
                                )}
                              />
                            </div>
                          </div>

                          {/* Advanced Filters */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              console.log('No filter')
                            }}>
                            <div>
                              <RadioButton
                                text='Advanced Filters'
                                isSelected={
                                  filter &&
                                  headerDef.filterType === 'advancedFilters'
                                }
                                isDisabled={disabledFilterTypes.includes(
                                  'advanced'
                                )}
                              />
                            </div>
                          </div>
                        </Fragment>
                      ) : null}

                      {/* Date filters */}
                      {!isCustomFilter && dataType === 'date' ? (
                        <Fragment>
                          {/* > After Date */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              props.onFilterTypeUpdate(field, 'afterDate')
                            }}>
                            <div>
                              <RadioButton
                                text='> After Date'
                                isSelected={
                                  filter && headerDef.filterType === 'afterDate'
                                }
                              />
                            </div>
                          </div>

                          {/* >= On OR After Date */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              console.log('No filter')
                              console.log('field', field)
                              console.log('filterValue', filterValue)
                              console.log('filter', filter)
                              props.onFilterTypeUpdate(field, 'afterOrOnDate') // afterOrOnDate
                            }}>
                            <div>
                              <RadioButton
                                text='≥ On OR After Date'
                                isSelected={
                                  filter &&
                                  headerDef.filterType === 'afterOrOnDate'
                                }
                              />
                            </div>
                          </div>
                          {/* = On Date */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              console.log('No filter')
                              console.log('field', field)
                              console.log('filterValue', filterValue)
                              console.log('filter', filter)
                              props.onFilterTypeUpdate(field, 'onDate')
                            }}>
                            <div>
                              <RadioButton
                                text='= On Date'
                                isSelected={
                                  filter && headerDef.filterType === 'onDate'
                                }
                              />
                            </div>
                          </div>
                          {/* <= On OR Before Date */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              console.log('No filter')
                              console.log('field', field)
                              console.log('filterValue', filterValue)
                              console.log('filter', filter)
                              props.onFilterTypeUpdate(field, 'onOrBeforeDate')
                            }}>
                            <div>
                              <RadioButton
                                text='≤ On OR Before Date'
                                isSelected={
                                  filter &&
                                  headerDef.filterType === 'onOrBeforeDate'
                                }
                              />
                            </div>
                          </div>
                          {/* < Before Date */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              console.log('No filter')
                              console.log('field', field)
                              console.log('filterValue', filterValue)
                              console.log('filter', filter)
                              props.onFilterTypeUpdate(field, 'beforeDate')
                            }}>
                            <div>
                              <RadioButton
                                text='< Before Date'
                                isSelected={
                                  filter &&
                                  headerDef.filterType === 'beforeDate'
                                }
                              />
                            </div>
                          </div>
                          {/* [] Date Range */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              console.log('No filter')
                              console.log('field', field)
                              console.log('filterValue', filterValue)
                              console.log('filter', filter)
                              props.onFilterTypeUpdate(field, 'dateRange')
                            }}>
                            <div>
                              <RadioButton
                                text='[ ] Date Range'
                                isSelected={
                                  filter && headerDef.filterType === 'dateRange'
                                }
                              />
                            </div>
                          </div>
                          {/* # of Days Ago */}
                          <div
                            className='m-item no-hover pl-4'
                            onClick={() => {
                              console.log('No filter')
                              console.log('field', field)
                              console.log('filterValue', filterValue)
                              console.log('filter', filter)
                              props.onFilterTypeUpdate(field, 'daysAgo')
                            }}>
                            <div>
                              <RadioButton
                                text='# of Days Ago'
                                isSelected={
                                  filter && headerDef.filterType === 'daysAgo'
                                }
                              />
                            </div>
                          </div>
                        </Fragment>
                      ) : null}
                    </div>
                  </PopupMenu>
                ) : null}

                {/* Pin combobox */}
                <div
                  ref={pinDropdown}
                  className={`mb-2 ${css(styles.filterTypeDropdown)}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setSubPinMenuVisibility(!isSubPinMenuVisible)
                    setSubFilterMenuVisibility(false)
                  }}>
                  <div className='fx cursor__pointer'>
                    <div className={`pl-1 ${css(styles.filterDropdownText)}`}>
                      {pinText}
                    </div>
                    <div className={css(styles.filterDropdownBtn)}>
                      <i className='fa fa-chevron-down' />
                    </div>
                  </div>
                </div>
                {/* Pin dropdown menu */}
                {isSubPinMenuVisible ? (
                  <PopupMenu
                    top={85}
                    left={10}
                    position='absolute'
                    visible={isSubPinMenuVisible}
                    parent={pinDropdown}
                    onOutsideClick={() => {
                      setSubPinMenuVisibility(false)
                    }}>
                    <div className='qtx-popup-menu' style={{ width: '228px' }}>
                      {/* No Pin */}
                      <div
                        className={`m-item ${pin === 'none' ? 'active' : ''}`}
                        onClick={() => {
                          props.onPinChange(headerDef.field, 'none')
                        }}>
                        {/* Icon */}
                        <div className='icon'>
                          <i
                            className='fa fa-thumb-tack'
                            style={{ fontSize: '16px' }}
                          />
                        </div>
                        {/* Text */}
                        <div>No Pin</div>
                      </div>

                      {/* Left Pin */}
                      <div
                        className={`m-item ${pin === 'left' ? 'active' : ''}`}
                        onClick={() => {
                          props.onPinChange(headerDef.field, 'left')
                        }}>
                        {/* Icon */}
                        <div className='icon'>
                          <i
                            className='fa fa-thumb-tack'
                            style={{
                              fontSize: '16px',
                              transform: 'rotate(30deg)'
                            }}
                          />
                        </div>
                        {/* Text */}
                        <div>Pin to LEFT</div>
                      </div>

                      {/* Pin Right */}
                      <div
                        className={`m-item ${pin === 'right' ? 'active' : ''}`}
                        style={{ borderBottom: '1px solid #082840' }}
                        onClick={() => {
                          props.onPinChange(headerDef.field, 'right')
                        }}>
                        {/* Icon */}
                        <div className='icon'>
                          <i
                            className='fa fa-thumb-tack'
                            style={{
                              fontSize: '16px',
                              transform: 'rotate(-30deg)'
                            }}
                          />
                        </div>
                        {/* Text */}
                        <div>Pin to RIGHT</div>
                      </div>
                    </div>
                  </PopupMenu>
                ) : null}

                {/* Row text alignment */}
                <div
                  className='m-item no-hover'
                  style={{ display: 'none' }}
                  onClick={(e) => {
                    e.stopPropagation()
                  }}>
                  {/* Text */}
                  <div
                    className='ml-2'
                    style={{
                      fontSize: '13px',
                      minWidth: '70px'
                    }}>
                    Row Text
                  </div>
                  {/* Horizontal alignement */}
                  <div
                    className='fx ml-2'
                    style={{ border: '1px solid black' }}>
                    {/* Align left */}
                    <div
                      className={`small-icon ${
                        colCellsStyles.textAlign === 'left' ? 'selected' : ''
                      }`}
                      style={{ borderRight: '1px solid black' }}
                      onClick={() => {
                        props.onChangeTextAlignment(headerDef.field, 'left')
                      }}>
                      <i className='fa fa-align-left' />
                    </div>
                    {/* Align center */}
                    <div
                      className={`small-icon ${
                        colCellsStyles.textAlign === 'center' ? 'selected' : ''
                      }`}
                      style={{ borderRight: '1px solid black' }}
                      onClick={() => {
                        props.onChangeTextAlignment(
                          props.headerDef.field,
                          'center'
                        )
                      }}>
                      <i className='fa fa-align-center' />
                    </div>
                    {/* Align right */}
                    <div
                      className={`small-icon ${
                        colCellsStyles.textAlign === 'right' ? 'selected' : ''
                      }`}
                      style={{ borderRight: '1px solid black' }}
                      onClick={() => {
                        props.onChangeTextAlignment(
                          props.headerDef.field,
                          'right'
                        )
                      }}>
                      <i className='fa fa-align-right' />
                    </div>
                  </div>
                </div>
              </div>
            </PopupMenu>
          </div>
        </div>

        {/* Bottom row - Filter UI */}
        <div
          className='fx header-bottom-row'
          style={{
            marginBottom: '2px',
            flexFlow: 'column nowrap',
            flexShrink: '0',
            display: props.mergeHeaderRows ? 'none' : 'flex'
          }}>
          {/* Filter/Filter Text/Filter Menu */}
          <div
            className='noselect'
            style={{
              height: filter ? '20px' : 'auto',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              marginRight: '4px'
            }}>
            {/* String filters */}
            {filter && dataType === 'string' ? (
              <div className='fx' style={{ position: 'relative' }}>
                {/* <i
                  className='fa fa-filter'
                  style={{
                    padding: '4px 4px 0 0',
                    color: filterValue.length > 0 ? '#0080FF' : gridHeaderTextColor
                  }}
                /> */}
                <TextInput
                  placeholder={filterText}
                  placeholderColor='yellow'
                  value={filterValue}
                  onChange={handleOnFilterValueChanged}
                  style={{
                    width: `${headerDef.colStyles.minWidth - 12}px`,
                    height: '20px',
                    background: '#103a60',
                    border: '1px solid #60A0E0',
                    borderWidth: '0 0 1px 0',
                    marginBottom: '2px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}
                />
                <i
                  className='fa fa-times'
                  style={{ position: 'relative', top: '4px', left: '-12px' }}
                  onClick={() => {
                    const { filterType, field, dataType } = props.headerDef
                    setFilterValue('')
                    debouncedApplyStringFilter(dataType, filterType, field, '')
                  }}
                />
              </div>
            ) : null}

            {/* Number filters */}
            {filter && dataType === 'number' ? (
              <Fragment>
                {dataType === 'number' &&
                headerDef.filterType === 'numberRange' ? (
                  <div className='fx'>
                    <div className={`mr-1 ${css(styles.filterHintIcon)}`}>
                      {' '}
                      []{' '}
                    </div>
                    {/* Lower range */}
                    <TextInput
                      placeholder={filterText}
                      placeholderColor='yellow'
                      value={lowerRange}
                      onChange={handleOnChangeLowerRange}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter') {
                          handleOnApplyFilter()
                        }
                      }}
                      style={{
                        width: '60px',
                        height: '20px',
                        background: '#103a60',
                        border: '1px solid #60A0E0',
                        borderWidth: '0 0 1px 0',
                        marginBottom: '2px',
                        fontSize: '10px'
                      }}
                    />
                    <div className='ml-1 mr-1'>-</div>
                    {/* Upper range */}
                    <TextInput
                      placeholder={filterText}
                      placeholderColor='yellow'
                      value={upperRange}
                      onChange={handleOnChangeUpperRange}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter') {
                          handleOnApplyFilter()
                        }
                      }}
                      style={{
                        width: '60px',
                        height: '20px',
                        background: '#103a60',
                        border: '1px solid #60A0E0',
                        borderWidth: '0 0 1px 0',
                        marginBottom: '2px',
                        fontSize: '10px'
                      }}
                    />
                  </div>
                ) : null}

                {dataType === 'number' &&
                headerDef.filterType !== 'numberRange' ? (
                  <div className='fx'>
                    {headerDef.filterType === 'greaterThan' ? (
                      <div className={`mr-1 ${css(styles.filterHintIcon)}`}>
                        {' '}
                        &gt;{' '}
                      </div>
                    ) : null}

                    {headerDef.filterType === 'greaterThanOrEqualsTo' ? (
                      <div className={`mr-1 ${css(styles.filterHintIcon)}`}>
                        {' '}
                        ≥{' '}
                      </div>
                    ) : null}

                    {headerDef.filterType === 'equalsTo' ? (
                      <div className={`mr-1 ${css(styles.filterHintIcon)}`}>
                        {' '}
                        ={' '}
                      </div>
                    ) : null}

                    {headerDef.filterType === 'lessThanOrEqualsTo' ? (
                      <div className={`mr-1 ${css(styles.filterHintIcon)}`}>
                        {' '}
                        ≤{' '}
                      </div>
                    ) : null}

                    {headerDef.filterType === 'lessThan' ? (
                      <div className={`mr-1 ${css(styles.filterHintIcon)}`}>
                        {' '}
                        &lt;{' '}
                      </div>
                    ) : null}

                    <TextInput
                      placeholder={filterText}
                      placeholderColor='yellow'
                      value={filterValue}
                      onChange={(e) => {
                        const { value } = e.target
                        setFilterValue(value)
                      }}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter') {
                          handleOnApplyFilter()
                        }
                      }}
                      style={{
                        width: '120px',
                        height: '20px',
                        background: '#103a60',
                        border: '1px solid #60A0E0',
                        borderWidth: '0 0 1px 0',
                        marginBottom: '2px',
                        fontSize: '10px'
                      }}
                    />
                  </div>
                ) : null}
              </Fragment>
            ) : null}

            {/* Date filters */}
            {filter && dataType === 'date' ? (
              <Fragment>
                {/* Days Ago */}
                {headerDef.filterType === 'daysAgo' ? (
                  <div>
                    <TextInput
                      placeholder='# of Days Ago'
                      placeholderColor='yellow'
                      value={filterValue}
                      onChange={(e) => {
                        const { value } = e.target
                        setFilterValue(value)
                      }}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter') {
                          handleOnApplyFilter()
                        }
                      }}
                      style={{
                        width: '100%',
                        height: '20px',
                        background: '#103a60',
                        border: '1px solid #60A0E0',
                        borderWidth: '0 0 1px 0',
                        marginBottom: '2px',
                        fontSize: '10px'
                      }}
                    />
                  </div>
                ) : null}
                {/* Date filter */}
                {headerDef.filterType !== 'daysAgo' ? (
                  <div className='fx' style={{ position: 'relative' }}>
                    {/* > >= = < <= date filters */}
                    {headerDef.filterType !== 'dateRange' ? (
                      <div
                        className='fx'
                        style={{
                          position: 'relative',
                          width: '55px',
                          maxWidth: '55px',
                          fontSize: '12px'
                        }}>
                        {headerDef.filterType === 'afterDate' ? (
                          <div className={`mr-1 ${css(styles.filterHintIcon)}`}>
                            {' '}
                            &gt;{' '}
                          </div>
                        ) : null}

                        {headerDef.filterType === 'afterOrOnDate' ? (
                          <div className={`mr-1 ${css(styles.filterHintIcon)}`}>
                            {' '}
                            ≥{' '}
                          </div>
                        ) : null}

                        {headerDef.filterType === 'onDate' ? (
                          <div className={`mr-1 ${css(styles.filterHintIcon)}`}>
                            {' '}
                            ={' '}
                          </div>
                        ) : null}

                        {headerDef.filterType === 'onOrBeforeDate' ? (
                          <div className={`mr-1 ${css(styles.filterHintIcon)}`}>
                            {' '}
                            ≤{' '}
                          </div>
                        ) : null}

                        {headerDef.filterType === 'beforeDate' ? (
                          <div className={`mr-1 ${css(styles.filterHintIcon)}`}>
                            {' '}
                            &lt;{' '}
                          </div>
                        ) : null}

                        <DatePicker
                          year={
                            filterValue.length > 0
                              ? new Date(filterValue).getFullYear()
                              : new Date().getFullYear()
                          }
                          month={
                            filterValue.length > 0
                              ? new Date(filterValue).getMonth()
                              : new Date().getMonth()
                          }
                          date={
                            filterValue.length > 0
                              ? new Date(filterValue).getDate()
                              : new Date().getDate()
                          }
                          color='#0080FF'
                          left={pin === 'left' ? 8 : null}
                          right={pin !== 'left' ? 1 : null}
                          showPristineState={filterValue.length === 0}
                          onChangeDate={handleOnDateChanged}
                        />
                      </div>
                    ) : null}

                    {/* Date range */}
                    {headerDef.filterType === 'dateRange' ? (
                      <div
                        className='fx'
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: `${headerDef.colStyles.minWidth - 8}px`,
                          padding: '0 4px',
                          fontSize: '12px'
                        }}>
                        <DatePicker
                          year={
                            filterValue.length > 0
                              ? lowerDate.getFullYear()
                              : new Date().getFullYear()
                          }
                          month={
                            filterValue.length > 0
                              ? lowerDate.getMonth()
                              : new Date().getMonth()
                          }
                          date={
                            filterValue.length > 0
                              ? lowerDate.getDate()
                              : new Date().getDate()
                          }
                          color='#0080FF'
                          left={pin === 'left' ? 8 : null}
                          right={pin !== 'left' ? 1 : null}
                          // showPristineState={filterValue.length > 0 && lowerDateVal.length === 0}
                          showPristineState={isLowerDatePristine}
                          onChangeDate={(year, month, date) => {
                            handleOnDateRangeChanged(
                              0,
                              `${month + 1}/${date}/${year}`
                            )
                          }}
                        />
                        <div className='ml-1 mr-1'>to</div>
                        <DatePicker
                          year={
                            filterValue.length > 0
                              ? upperDate.getFullYear()
                              : new Date().getFullYear()
                          }
                          month={
                            filterValue.length > 0
                              ? upperDate.getMonth()
                              : new Date().getMonth()
                          }
                          date={
                            filterValue.length > 0
                              ? upperDate.getDate()
                              : new Date().getDate()
                          }
                          color='#0080FF'
                          left={pin === 'left' ? 8 : null}
                          right={pin !== 'left' ? 1 : null}
                          // showPristineState={filterValue.length > 0 && (upperDateVal && upperDateVal.length === 0)}
                          showPristineState={isUpperDatePrisitine}
                          onChangeDate={(year, month, date) => {
                            handleOnDateRangeChanged(
                              1,
                              `${month + 1}/${date}/${year}`
                            )
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </Fragment>
            ) : null}
            {/* Select filter */}
            {filter && dataType === 'select' ? (
              <div
                ref={selectMenu}
                className='fx'
                style={{
                  alignItems: 'center',
                  position: 'relative'
                }}>
                <i
                  className='fa fa-filter'
                  style={{
                    padding: '4px 4px 0 0',
                    color:
                      filterValue.length > 0 ? '#0080FF' : gridHeaderTextColor
                  }}
                />
                <div
                  className='text-truncate ml-1'
                  style={{ width: `${width - 50}px` }}
                  onClick={() => setIsSelectMenuOpen(true)}>
                  {`(${selectItems.reduce((prv, curr) =>
                    prv.hasOwnProperty('checked')
                      ? (prv.checked ? 1 : 0) + (curr.checked ? 1 : 0)
                      : prv + (curr.checked ? 1 : 0)
                  )}) ${
                    selectItems.reduce((prv, curr) =>
                      prv.hasOwnProperty('checked')
                        ? (prv.checked ? 1 : 0) + (curr.checked ? 1 : 0)
                        : prv + (curr.checked ? 1 : 0)
                    ) === selectItems.length
                      ? 'ALL'
                      : selectFilterText.substring(
                          0,
                          selectFilterText.length - 1
                        )
                  }`}
                </div>
                <i
                  className='ml-2 fa fa-caret-down'
                  onClick={() => setIsSelectMenuOpen(true)}
                />
                <PopupMenu
                  top={26}
                  left={pin === 'left' ? 1 : null}
                  right={pin !== 'left' ? 1 : null}
                  visible={isSelectMenuOpen}
                  parent={selectMenu}
                  onOutsideClick={() => {
                    setIsSelectMenuOpen(false)
                  }}
                  position='absolute'
                  // enableDyncmicPosition
                  offsetLeft={320}
                  parentContainerWidth={parseInt(gridWidth)}>
                  <div
                    className='qtx-popup-menu'
                    style={{ width: `${menuWidth}px`, background: '#002448' }}>
                    {/* Header */}
                    <div
                      style={{
                        paddingLeft: '8px',
                        height: '40px',
                        background: '#003060',
                        borderBottom: '1px solid #004080'
                      }}>
                      <TextInput
                        placeholder='Search...'
                        placeholderColor='yellow'
                        value={selectFilterSerachValue}
                        onChange={handleOnChangeSelectFilterSearchValue}
                        style={{
                          width: 'calc(100% - 16px)',
                          height: '30px',
                          background: '#003060',
                          border: '1px solid #60A0E0',
                          borderWidth: '0 0 1px 0',
                          marginBottom: '2px',
                          fontSize: '10px'
                        }}
                      />
                    </div>
                    {/* Body  */}
                    <div>
                      <>
                        <div
                          style={{
                            height: '32px',
                            paddingLeft: '8px',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                          <Checkbox
                            text={`Select (${selectItems.reduce((prv, curr) =>
                              prv.hasOwnProperty('checked')
                                ? (prv.checked ? 1 : 0) + (curr.checked ? 1 : 0)
                                : prv + (curr.checked ? 1 : 0)
                            )} of ${selectItems.length})`}
                            isChecked={
                              selectItems.reduce((prv, curr) =>
                                prv.hasOwnProperty('checked')
                                  ? (prv.checked ? 1 : 0) +
                                    (curr.checked ? 1 : 0)
                                  : prv + (curr.checked ? 1 : 0)
                              ) === selectItems.length
                            }
                            isIndeterminate={
                              selectItems.reduce((prv, curr) =>
                                prv.hasOwnProperty('checked')
                                  ? (prv.checked ? 1 : 0) +
                                    (curr.checked ? 1 : 0)
                                  : prv + (curr.checked ? 1 : 0)
                              ) > 0 &&
                              selectItems.reduce((prv, curr) =>
                                prv.hasOwnProperty('checked')
                                  ? (prv.checked ? 1 : 0) +
                                    (curr.checked ? 1 : 0)
                                  : prv + (curr.checked ? 1 : 0)
                              ) !== selectItems.length
                            }
                            onChange={() => {
                              onSelectAllChange(field)
                            }}
                          />
                        </div>
                        <div
                          style={{
                            maxHeight: '200px',
                            overflowY: 'auto'
                          }}>
                          {selectItems.map((el) => {
                            if (
                              el.value
                                .toLowerCase()
                                .startsWith(
                                  selectFilterSerachValue.toLowerCase()
                                )
                            ) {
                              return (
                                <div
                                  key={uuidv4()}
                                  style={{
                                    height: '32px',
                                    paddingLeft: '8px',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}>
                                  <Checkbox
                                    // text={el.value}
                                    text=''
                                    isChecked={el.checked}
                                    onChange={() => {
                                      props.onSelectItemChange(field, el.value)
                                    }}
                                  />
                                  <div
                                    className='text-truncate'
                                    style={{ fontSize: '10px' }}>
                                    {el.value}
                                  </div>
                                </div>
                              )
                            }
                          })}
                        </div>
                      </>
                    </div>
                    {/* Footer */}
                    {/* <div
                        style={{
                          height: '48px',
                          background: '#003060',
                          borderTop: '1px solid #004080',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          padding: '0 8px'
                        }}
                      >
                        <div className='mr-2'>
                          <svg width="18" height="21" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M17.76 9.971a.703.703 0 1 0-1.386.23c.062.372.094.778.094 1.178 0 1.961-.764 3.805-2.15 5.192a7.293 7.293 0 0 1-5.194 2.148 7.298 7.298 0 0 1-5.193-2.15 7.298 7.298 0 0 1-2.15-5.193c0-1.961.764-3.807 2.15-5.193a7.298 7.298 0 0 1 5.44-2.147L7.772 5.634a.701.701 0 0 0 0 .994c.275.275.72.275.994 0l2.652-2.652a.701.701 0 0 0 0-.994L8.767.332a.701.701 0 0 0-.994 0 .701.701 0 0 0 0 .994l1.3 1.3A8.724 8.724 0 0 0 2.94 5.188a8.724 8.724 0 0 0-2.564 6.188 8.72 8.72 0 0 0 2.562 6.187 8.722 8.722 0 0 0 6.187 2.562c1.182 0 2.326-.23 3.406-.688a8.686 8.686 0 0 0 2.781-1.872 8.736 8.736 0 0 0 1.875-2.78 8.679 8.679 0 0 0 .688-3.405 8.7 8.7 0 0 0-.114-1.409Z" fill="#00A0FF" fillOpacity=".5"/></svg>
                        </div>
                        <Button
                          btnType='primary'
                          isOutlineBtn
                          style={{
                            height: '32px',
                            width: '85px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '8px'
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          btnType='primary'
                          style={{
                            height: '32px',
                            width: '85px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          Accept
                        </Button>
                      </div> */}
                  </div>
                </PopupMenu>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}

GridHeaderCell.propTypes = propTypes
GridHeaderCell.defaultProps = {
  mergeHeaderRows: false,
  headerHeight: 0
}
export { GridHeaderCell }
